import React, { type MouseEventHandler } from 'react'
import { Link } from 'react-router-dom'

import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { ActivityIndicator } from '../ActivityIndicator'
import { Icon } from '../Icon'
import { Loading } from '../Loading'
import { Tooltip } from '../Tooltip'
import * as S from './styled'
import type { ToolbarButtonProps } from './types'

const getComponent = ({ to, href }: Pick<ToolbarButtonProps, 'to' | 'href'>) => {
  if (to) return Link

  if (href) return 'a'

  return 'button'
}

export const ToolbarButton = ({
  label,
  to,
  href,
  target,
  placement,
  variant = 'transparent',
  size = 'medium',
  icon,
  iconSize = 16,
  color,
  active = false,
  activityIndicator,
  activityIndicatorCount,
  disabled = false,
  isLoading = false,
  onClick,
  testid = 'ToolbarButton.Button',
}: ToolbarButtonProps) => {
  const media = useMediaQueryContext()
  const handleClick: MouseEventHandler = (event) => {
    // This prevents Table row to be clicked when Dropdown component is triggered using ToolbarButton.
    if (!to) {
      event.preventDefault()
    }

    if (!onClick) return

    onClick()
  }

  return (
    <Tooltip
      placement={placement}
      reference={
        <Loading isLoading={isLoading} size="small">
          <S.Container
            as={getComponent({ to, href })}
            href={href}
            to={to}
            target={target}
            $variant={variant}
            $size={size}
            $active={active}
            $color={color}
            type={href ? undefined : 'button'}
            disabled={disabled}
            onClick={handleClick}
            data-testid={testid}
          >
            <Icon component={icon} height={iconSize} />
            {activityIndicator && !active && (
              <S.ActivityIndicatorWrapper>
                <ActivityIndicator>{activityIndicatorCount}</ActivityIndicator>
              </S.ActivityIndicatorWrapper>
            )}
          </S.Container>
        </Loading>
      }
    >
      {!media.laptop || active || disabled ? null : label}
    </Tooltip>
  )
}
